import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-10 px-4">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="flex items-center text-teal-400 hover:text-teal-300 mb-4 text-sm">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-teal-400" />
            <h1 className="text-3xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-slate-400 mt-2">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="prose prose-slate max-w-none">
          
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-3">What We Collect</h2>
            <p className="text-slate-700 text-sm leading-relaxed mb-3">
              When you use Audiostride, we collect the following to provide and improve our audio tour experiences:
            </p>
            <ul className="text-slate-700 text-sm space-y-2 list-disc list-inside">
              <li><strong>Account information:</strong> Your email address and name when you create an account or redeem a promo code.</li>
              <li><strong>Tour activity:</strong> Which tours you start, which stops you listen to, how long you listen, and whether you complete the tour.</li>
              <li><strong>Device information:</strong> Whether you're on a mobile device or desktop, and general browser type.</li>
              <li><strong>Location data:</strong> GPS coordinates are used in real-time to trigger audio at tour stops. We do not store your location history.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-3">How We Use Your Data</h2>
            <ul className="text-slate-700 text-sm space-y-2 list-disc list-inside">
              <li>To deliver the audio tour experience (playing the right audio at the right location).</li>
              <li>To understand how tours are used so we can make them better.</li>
              <li>To generate aggregated analytics for our venue partners (museums, cultural sites). These reports contain engagement trends and patterns — <strong>never your personal information</strong>.</li>
              <li>To identify unique and repeat visitors for our own internal understanding of tour performance.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-3">What We Share</h2>
            <p className="text-slate-700 text-sm leading-relaxed mb-3">
              We share <strong>aggregated, anonymous analytics</strong> with our venue partners. This includes things like:
            </p>
            <ul className="text-slate-700 text-sm space-y-1 list-disc list-inside mb-3">
              <li>"47 unique visitors this month"</li>
              <li>"Most popular stop: The Main Gallery"</li>
              <li>"Average tour completion: 72%"</li>
            </ul>
            <p className="text-slate-700 text-sm leading-relaxed font-semibold">
              We never share your email, name, or any personally identifiable information with anyone.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-3">What We Don't Do</h2>
            <ul className="text-slate-700 text-sm space-y-2 list-disc list-inside">
              <li>We don't sell your data.</li>
              <li>We don't share your email with third parties.</li>
              <li>We don't store your GPS location history.</li>
              <li>We don't use your data for advertising.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-3">Data Security</h2>
            <p className="text-slate-700 text-sm leading-relaxed">
              Your data is stored securely using industry-standard encryption and access controls. Only authorized Audiostride administrators have access to personal data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-3">Your Rights</h2>
            <p className="text-slate-700 text-sm leading-relaxed mb-3">
              You have the right to:
            </p>
            <ul className="text-slate-700 text-sm space-y-2 list-disc list-inside">
              <li>Request a copy of your data.</li>
              <li>Request deletion of your data.</li>
              <li>Unsubscribe from any communications.</li>
            </ul>
            <p className="text-slate-700 text-sm leading-relaxed mt-3">
              To exercise any of these rights, contact us at <a href="mailto:hello@audiostride.com" className="text-teal-600 underline">hello@audiostride.com</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-3">Changes to This Policy</h2>
            <p className="text-slate-700 text-sm leading-relaxed">
              We may update this policy from time to time. Any changes will be posted on this page with an updated date.
            </p>
          </section>

        </div>
      </main>

      <footer className="bg-slate-900 border-t border-slate-800 py-6 px-4">
        <p className="text-slate-600 text-xs text-center">
          &copy; {new Date().getFullYear()} Audiostride. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
